import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Eye,
  Users,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  Shield,
  ShoppingBag,
  MapPin,
  CheckCircle,
  XCircle,
  Pencil
} from 'lucide-react';

import toast from 'react-hot-toast';
import PasswordResetModal from '../../components/ui/PasswordResetModal';
import { motion as m } from 'framer-motion';
import EditUserModal from '../../components/ui/EditUserModal';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  
  const [passwordResetModal, setPasswordResetModal] = useState({
    isOpen: false,
    user: null,
    isSuccess: false,
    isLoading: false,
    error: null
  });
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [emailExistsModal, setEmailExistsModal] = useState({ isOpen: false, message: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, user: null, isLoading: false, error: null });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, selectedRole]);

  // Removed role dropdown menu logic

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        role: selectedRole
      });

      const response = await fetch(`/api/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.pagination.pages);
      } else {
        toast.error('Erreur lors du chargement des utilisateurs');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        toast.success("Statut de l'utilisateur mis à jour");
        fetchUsers(); // Refresh the list
      } else {
        toast.error('Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleViewOrders = async (user) => {
    try {
      setSelectedUser(user);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/admin/users/${user.id}/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserOrders(data.orders || []);
        setOrdersModalOpen(true);
      } else {
        toast.error("Impossible de charger l'historique des achats");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement de l'historique");
    }
  };

  // Role changes handled via Edit modal now

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
  };

  const handleSaveUser = async (form) => {
    // Create if no editingUser.id, else update existing
    try {
      setSavingEdit(true);
      const token = localStorage.getItem('token');
      let res;
      if (!editingUser || !editingUser.id) {
        // Create new user
        res = await fetch('/api/admin/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            password: form.password || '123456',
            phone: form.phone,
            address: form.address,
            city: form.city,
            postal_code: form.postal_code,
            country: form.country,
            role: form.role,
            is_active: form.is_active,
            email_verified: form.email_verified
          })
        });
      } else {
        // Update existing user
        if (form.role && form.role !== editingUser.role) {
          await fetch(`/api/admin/users/${editingUser.id}/role`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ role: form.role })
          });
        }
        res = await fetch(`/api/admin/users/${editingUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            phone: form.phone,
            address: form.address,
            city: form.city,
            postal_code: form.postal_code,
            country: form.country,
            is_active: form.is_active,
            email_verified: form.email_verified
          })
        });
      }
      if (res.ok) {
        toast.success(!editingUser || !editingUser.id ? 'Utilisateur créé' : 'Utilisateur mis à jour');
        closeEditModal();
        // Refresh list to reflect changes
        fetchUsers();
      } else {
        const data = await res.json().catch(() => ({}));
        if (res.status === 409 || (data.error && /email/i.test(data.error))) {
          setEmailExistsModal({ isOpen: true, message: data.error || "Cet email existe déjà dans la base de données. Veuillez en choisir un autre." });
        } else {
          toast.error(data.error || "Échec de l'enregistrement");
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteConfirm = (user) => {
    setDeleteConfirm({ isOpen: true, user, isLoading: false, error: null });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ isOpen: false, user: null, isLoading: false, error: null });
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirm.user) return;
    try {
      setDeleteConfirm(prev => ({ ...prev, isLoading: true }));
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/admin/users/${deleteConfirm.user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Utilisateur supprimé');
        closeDeleteConfirm();
        fetchUsers();
        setEditModalOpen(false);
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || 'Échec de la suppression');
        setDeleteConfirm(prev => ({ ...prev, isLoading: false, error: data.error }));
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la suppression');
      setDeleteConfirm(prev => ({ ...prev, isLoading: false, error: 'Erreur réseau' }));
    }
  };

  const handleResetPassword = async (userId, userName) => {
    try {
      console.log(`Starting password reset for user ${userId} (${userName})`);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setPasswordResetModal(prev => ({
          ...prev,
          error: 'Token d\'authentification manquant'
        }));
        return;
      }
      
      console.log('Token found, making API request...');
      
      const res = await fetch(`/api/admin/users/${userId}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ newPassword: '123456' })
      });
      
      console.log('API response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('API response data:', data);
        
        // Show success in modal
        setPasswordResetModal(prev => ({
          ...prev,
          isSuccess: true,
          isLoading: false,
          user: data.user
        }));
        
        // Log the success for debugging
        console.log(`Password reset successful for user ${userId} (${userName})`);
      } else {
        const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
        console.error('API error response:', errorData);
        setPasswordResetModal(prev => ({
          ...prev,
          error: errorData.error || 'Échec de la réinitialisation du mot de passe',
          isLoading: false
        }));
      }
    } catch (e) {
      console.error('Password reset error:', e);
      setPasswordResetModal(prev => ({
        ...prev,
        error: 'Erreur de connexion lors de la réinitialisation du mot de passe',
        isLoading: false
      }));
    }
  };

  const openPasswordResetModal = (user) => {
    console.log('Opening password reset modal for user:', user);
    setPasswordResetModal({
      isOpen: true,
      user: { 
        id: user.id,
        name: `${user.first_name} ${user.last_name}`, 
        email: user.email 
      },
      isSuccess: false,
      isLoading: false,
      error: null
    });
  };

  const closePasswordResetModal = (action) => {
    console.log('closePasswordResetModal called with action:', action);
    if (action === 'confirm') {
      // Start the password reset process
      const userId = passwordResetModal.user.id;
      const userName = passwordResetModal.user.name;
      console.log('Starting password reset for:', userId, userName);
      setPasswordResetModal(prev => ({ ...prev, isLoading: true }));
      handleResetPassword(userId, userName);
    } else {
      // Close the modal
      console.log('Closing modal');
      setPasswordResetModal({
        isOpen: false,
        user: null,
        isSuccess: false,
        isLoading: false,
        error: null
      });
    }
  };

  const handleConnectAsUser = async (user) => {
    try {
      // Store current admin session
      const currentAdminToken = localStorage.getItem('token');
      const currentAdminUser = localStorage.getItem('user');
      
      // Store admin session for later restoration
      localStorage.setItem('adminToken', currentAdminToken);
      localStorage.setItem('adminUser', currentAdminUser);
      
      toast.success(`Mode utilisateur activé pour ${user.first_name} ${user.last_name}`);
      setSelectedUser(user);
      setShowUserDetails(true);
      
    } catch (error) {
      console.error('Error connecting as user:', error);
      toast.error("Erreur lors de la connexion en tant qu'utilisateur");
    }
  };

  const handleRestoreAdminSession = () => {
    try {
      // Restore admin session
      const adminToken = localStorage.getItem('adminToken');
      const adminUser = localStorage.getItem('adminUser');
      
      if (adminToken && adminUser) {
        localStorage.setItem('token', adminToken);
        localStorage.setItem('user', adminUser);
        
        // Clean up
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        
        toast.success('Session administrateur restaurée');
        window.location.reload(); // Refresh to show admin view
      } else {
        toast.error('Session administrateur non trouvée');
      }
    } catch (error) {
      console.error('Error restoring admin session:', error);
      toast.error('Erreur lors de la restauration de la session admin');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-red-100 text-red-800', text: 'Administrateur' },
      customer: { color: 'bg-blue-100 text-blue-800', text: 'Client' },
      staff: { color: 'bg-green-100 text-green-800', text: 'Staff' }
    };
    
    const config = roleConfig[role] || { color: 'bg-gray-100 text-gray-800', text: role };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Gestion des Utilisateurs</h1>
              <p className="text-gray-400">Gérez les comptes utilisateurs et leurs permissions</p>
            </div>
            
            {/* Restore Admin Session Button */}
            {localStorage.getItem('adminToken') && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={handleRestoreAdminSession}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Shield className="h-4 w-4" />
                Restaurer Session Admin
              </motion.button>
            )}
            {!localStorage.getItem('adminToken') && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => {
                  setEditingUser({});
                  setEditModalOpen(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                + Ajouter un utilisateur
              </motion.button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-800 rounded-lg p-6 border border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Total Utilisateurs</p>
                <p className="text-2xl font-bold text-white">{users.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gray-800 rounded-lg p-6 border border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-green-500 rounded-lg">
                <UserCheck className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Utilisateurs Actifs</p>
                <p className="text-2xl font-bold text-white">
                  {users.filter(user => user.is_active).length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gray-800 rounded-lg p-6 border border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-red-500 rounded-lg">
                <UserX className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Utilisateurs Inactifs</p>
                <p className="text-2xl font-bold text-white">
                  {users.filter(user => !user.is_active).length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gray-800 rounded-lg p-6 border border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-yellow-500 rounded-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Administrateurs</p>
                <p className="text-2xl font-bold text-white">
                  {users.filter(user => user.role === 'admin').length}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters, Search and View Toggle */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, prénom ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4 items-center">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="">Tous les rôles</option>
                <option value="admin">Administrateur</option>
                <option value="customer">Client</option>
                <option value="staff">Staff</option>
              </select>
              
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRole('');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Réinitialiser
              </button>

              <div className="ml-2 inline-flex rounded-lg overflow-hidden border border-gray-600">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 text-sm ${viewMode==='table' ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
                >
                  Tableau
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-2 text-sm ${viewMode==='cards' ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
                >
                  Cartes
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Users List */}
        {viewMode === 'table' ? (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Téléphone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Pays
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Date d'inscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Dernière connexion
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {users.map((user, index) => (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-400 flex items-center">
                            <Mail className="h-4 w-4 mr-1" />
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {user.phone || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.is_active)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                        {user.country || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {formatDate(user.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {user.last_login ? (
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {formatDate(user.last_login)}
                        </div>
                      ) : (
                        <span className="text-gray-500">Jamais</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-green-400 hover:text-green-300 transition-colors"
                          title="Modifier"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleUserDetails(user)}
                          className="text-yellow-400 hover:text-yellow-300 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleViewOrders(user)}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                          title="Historique d'achats"
                        >
                          <ShoppingBag className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleStatusToggle(user.id, user.is_active)}
                          className={`${
                            user.is_active 
                              ? 'text-red-400 hover:text-red-300' 
                              : 'text-green-400 hover:text-green-300'
                          } transition-colors`}
                          title={user.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {user.is_active ? <UserX className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
                        </button>
                        {/* Kebab menu removed; actions moved to edit modal */}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {users.map((user, index) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                    <span className="text-base font-semibold text-white">{user.first_name?.[0]}{user.last_name?.[0]}</span>
                  </div>
                  <div>
                    <div className="text-white font-medium">{user.first_name} {user.last_name}</div>
                    <div className="text-gray-300 text-sm flex items-center"><Mail className="h-4 w-4 mr-1" />{user.email}</div>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  {getRoleBadge(user.role)}
                  <div>{getStatusBadge(user.is_active)}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-gray-300">
                <div className="flex items-center"><Phone className="h-4 w-4 mr-2 text-gray-400" />{user.phone || '—'}</div>
                <div className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-gray-400" />{user.country || '—'}</div>
                <div className="flex items-center"><Calendar className="h-4 w-4 mr-2 text-gray-400" />Inscrit: {formatDate(user.created_at)}</div>
                <div className="flex items-center"><Calendar className="h-4 w-4 mr-2 text-gray-400" />Dernière connexion: {user.last_login ? formatDate(user.last_login) : 'Jamais'}</div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button onClick={() => handleUserDetails(user)} className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg">Détails</button>
                  <button onClick={() => openEditModal(user)} className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg">Modifier</button>
                  <button onClick={() => handleViewOrders(user)} className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Achats</button>
                </div>
                <button
                  onClick={() => handleStatusToggle(user.id, user.is_active)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${user.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                >
                  {user.is_active ? 'Désactiver' : 'Activer'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Page {currentPage} sur {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetails && selectedUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
              </div>
              
              <div className="inline-block align-bottom bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <Users className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-white">
                        Détails de l'utilisateur
                      </h3>
                      <div className="mt-4 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Nom complet</label>
                          <p className="mt-1 text-sm text-white">{selectedUser.first_name} {selectedUser.last_name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Email</label>
                          <p className="mt-1 text-sm text-white">{selectedUser.email}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Téléphone</label>
                          <p className="mt-1 text-sm text-white">{selectedUser.phone || '—'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Rôle</label>
                          <div className="mt-1">{getRoleBadge(selectedUser.role)}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Statut</label>
                          <div className="mt-1">{getStatusBadge(selectedUser.is_active)}</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-400">Adresse</label>
                            <p className="mt-1 text-sm text-white">{selectedUser.address || '—'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-400">Ville</label>
                            <p className="mt-1 text-sm text-white">{selectedUser.city || '—'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-400">Code postal</label>
                            <p className="mt-1 text-sm text-white">{selectedUser.postal_code || '—'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-400">Pays</label>
                            <p className="mt-1 text-sm text-white">{selectedUser.country || '—'}</p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Date d'inscription</label>
                          <p className="mt-1 text-sm text-white">{formatDate(selectedUser.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Dernière mise à jour</label>
                          <p className="mt-1 text-sm text-white">{selectedUser.updated_at ? formatDate(selectedUser.updated_at) : '—'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400">Email vérifié</label>
                          <div className="mt-1 flex items-center gap-2 text-sm text-white">
                            {selectedUser.email_verified ? (
                              <span className="inline-flex items-center text-green-400"><CheckCircle className="h-4 w-4 mr-1" /> Oui</span>
                            ) : (
                              <span className="inline-flex items-center text-red-400"><XCircle className="h-4 w-4 mr-1" /> Non</span>
                            )}
                          </div>
                        </div>
                        {selectedUser.last_login && (
                          <div>
                            <label className="block text-sm font-medium text-gray-400">Dernière connexion</label>
                            <p className="mt-1 text-sm text-white">{formatDate(selectedUser.last_login)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => setShowUserDetails(false)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orders History Modal */}
        {ordersModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="fixed inset-0 bg-gray-900/80" onClick={() => setOrdersModalOpen(false)}></div>
              <div className="relative bg-gray-800 rounded-lg border border-gray-700 w-full max-w-3xl p-6 z-10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">Historique d'achats — {selectedUser?.first_name} {selectedUser?.last_name}</h3>
                  <button className="text-gray-400 hover:text-white" onClick={() => setOrdersModalOpen(false)}>Fermer</button>
                </div>
                {userOrders.length === 0 ? (
                  <div className="text-gray-400">Aucune commande trouvée.</div>
                ) : (
                  <div className="space-y-4">
                    {userOrders.map(order => (
                      <div key={order.id} className="border border-gray-700 rounded-lg">
                        <div className="p-4 bg-gray-700/50 flex items-center justify-between">
                          <div className="text-white font-medium">#{order.order_number}</div>
                          <div className="text-sm text-gray-300">{new Date(order.created_at).toLocaleString('fr-FR')}</div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between text-sm text-gray-300 mb-3">
                            <span>Statut: <strong className="ml-1 text-white">{order.status}</strong></span>
                            <span>Paiement: <strong className="ml-1 text-white">{order.payment_status}</strong></span>
                            <span>Total: <strong className="ml-1 text-yellow-400">{Number(order.total_amount).toFixed(2)} €</strong></span>
                          </div>
                          {(order.payer_email || order.payment_method) && (
                            <div className="text-xs text-gray-400 mb-3">
                              {order.payment_method && (
                                <span className="mr-4">Méthode: <span className="text-gray-300">{order.payment_method}</span></span>
                              )}
                              {order.payer_email && (
                                <span>Payer PayPal: <span className="text-gray-300">{order.payer_email}</span></span>
                              )}
                            </div>
                          )}
                          <div className="border-t border-gray-700 pt-3">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-gray-400">
                                  <th className="text-left py-1">Produit</th>
                                  <th className="text-right py-1">Qté</th>
                                  <th className="text-right py-1">Prix</th>
                                  <th className="text-right py-1">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(order.items||[]).map((it,idx) => (
                                  <tr key={idx} className="text-gray-300">
                                    <td className="py-1">{it.product_name}</td>
                                    <td className="text-right py-1">{it.quantity}</td>
                                    <td className="text-right py-1">{Number(it.unit_price).toFixed(2)} €</td>
                                    <td className="text-right py-1">{Number(it.total_price).toFixed(2)} €</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {(order.payment_status === 'paid' && order.payment_reference) && (
                            <div className="mt-3 flex justify-end">
                              <button
                                onClick={async () => {
                                  const token = localStorage.getItem('token');
                                  const ok = window.confirm('Confirmer le remboursement ?');
                                  if (!ok) return;
                                  const res = await fetch(`/api/admin/orders/${order.id}/refund`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({})
                                  });
                                  if (res.ok) {
                                    alert('Remboursement effectué.');
                                  } else {
                                    const data = await res.json().catch(() => ({}));
                                    alert(data.error || 'Échec du remboursement');
                                  }
                                }}
                                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg"
                              >
                                Rembourser
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit & Password Reset Modals */}
        <EditUserModal
          isOpen={editModalOpen}
          onClose={() => closeEditModal()}
          user={editingUser}
          onSave={handleSaveUser}
          saving={savingEdit}
          onResetPassword={(u) => openPasswordResetModal(u || editingUser)}
          onDelete={(u) => openDeleteConfirm(u || editingUser)}
        />
        <PasswordResetModal
          isOpen={passwordResetModal.isOpen}
          onClose={closePasswordResetModal}
          user={passwordResetModal.user}
          isSuccess={passwordResetModal.isSuccess}
          isLoading={passwordResetModal.isLoading}
          error={passwordResetModal.error}
        />

        {emailExistsModal.isOpen && (
          <div className="fixed inset-0 z-[10000] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 transition-opacity z-[9999]" aria-hidden="true" onClick={() => setEmailExistsModal({ isOpen: false, message: '' })}>
                <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
              </m.div>
              <m.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.2 }} className="relative z-[10001] inline-block align-bottom bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700">
                <div className="bg-gradient-to-r from-yellow-600 to-orange-600 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">Email déjà utilisé</h3>
                </div>
                <div className="px-6 py-6 space-y-3">
                  <p className="text-gray-200">{emailExistsModal.message}</p>
                  <p className="text-gray-400 text-sm">Veuillez modifier l'adresse email dans le formulaire puis réessayer.</p>
                </div>
                <div className="px-6 py-4 bg-gray-700 flex justify-end">
                  <button onClick={() => setEmailExistsModal({ isOpen: false, message: '' })} className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg">OK</button>
                </div>
              </m.div>
            </div>
          </div>
        )}

        {deleteConfirm.isOpen && (
          <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 transition-opacity z-[9998]" aria-hidden="true" onClick={closeDeleteConfirm}>
                <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
              </m.div>
              <m.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.2 }} className="relative z-[9999] inline-block align-bottom bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700">
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">Confirmer la suppression</h3>
                </div>
                <div className="px-6 py-6">
                  <p className="text-gray-300">Voulez-vous vraiment supprimer l'utilisateur <span className="text-white font-semibold">{deleteConfirm.user?.first_name} {deleteConfirm.user?.last_name}</span> ? Cette action est irréversible.</p>
                </div>
                <div className="px-6 py-4 bg-gray-700 flex justify-end gap-3">
                  <button onClick={closeDeleteConfirm} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg">Annuler</button>
                  <button onClick={confirmDeleteUser} disabled={deleteConfirm.isLoading} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-60">{deleteConfirm.isLoading ? 'Suppression…' : 'Supprimer'}</button>
                </div>
              </m.div>
            </div>
          </div>
        )}
      </div>
    );
  };

export default AdminUsersPage;
